import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Eye, Loader2, RefreshCw, Save, Search, Upload } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultScrapeItemKeys = [
  "sku",
  "description",
  "category",
  "product_type",
  "colour",
  "size_cm",
  "assembled_weight_kg",
  "images",
  "name",
  "price",
  "rrp_price",
  "gtm_item_name",
  "gtm_item_sku",
  "gtm_item_category",
  "gtm_item_price",
  "gtm_item_list_name",
  "gtm_page_title",
];

const smartDefaultMap = {
  name: ["name", "gtm_item_name"],
  sku: ["sku", "gtm_item_sku", "gtm_magento_sku", "gtm_item_id"],
  description: ["description", "short_description"],
  category: ["category", "gtm_item_category", "gtm_item_category2"],
  categories: ["categories", "category"],
  collection_name: ["collection_name", "gtm_item_list_name"],
  product_type: ["product_type"],
  colour: ["colour"],
  size_cm: ["size_cm"],
  assembled_weight_kg: ["assembled_weight_kg"],
  price_numeric: ["price_numeric", "gtm_item_price", "gtm_value"],
  price: ["price", "gtm_item_price", "gtm_value"],
  images: ["images"],
};

const prioritySlots = [0, 1, 2];

const cleanMappings = (mappings) =>
  Object.fromEntries(
    Object.entries(mappings)
      .map(([target, sources]) => [target, (sources || []).filter(Boolean)])
      .filter(([, sources]) => sources.length > 0)
  );

const resolveMappedValue = (sourceValues, sourceFields = []) => {
  for (const sourceField of sourceFields) {
    const value = sourceValues?.[sourceField];
    if (Array.isArray(value) && value.length > 0) return value;
    if (typeof value === "string" && value.trim() !== "") return value;
    if (typeof value === "number" || typeof value === "boolean") return value;
    if (value && typeof value === "object" && Object.keys(value).length > 0) return value;
  }
  return null;
};

const buildSmartMapping = (targetFields, sourceFields) => {
  const sourceSet = new Set(sourceFields);
  const mapping = {};

  targetFields.forEach((field) => {
    const key = field.key;
    const defaults = smartDefaultMap[key] || [key];
    const resolved = defaults.filter((source) => sourceSet.has(source)).slice(0, 3);
    mapping[key] = [
      resolved[0] || "",
      resolved[1] || "",
      resolved[2] || "",
    ];
  });

  return mapping;
};

export const EnhancedMappingTab = () => {
  const [sourceUrl, setSourceUrl] = useState("https://www.boca-living.com/collections.html");
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [sourceCollections, setSourceCollections] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [scrapeItemOptions, setScrapeItemOptions] = useState([]);
  const [selectedScrapeItems, setSelectedScrapeItems] = useState(defaultScrapeItemKeys);
  const [selectedCollectionUrls, setSelectedCollectionUrls] = useState([]);
  const [collectionSearch, setCollectionSearch] = useState("");

  const [previewJob, setPreviewJob] = useState(null);
  const [startingPreview, setStartingPreview] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [mappings, setMappings] = useState({});
  const [targetFieldSearch, setTargetFieldSearch] = useState("");
  const [replaceExistingImages, setReplaceExistingImages] = useState(true);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  const [templateName, setTemplateName] = useState("Supplier Default");
  const [templateIsDefault, setTemplateIsDefault] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const [rowSearch, setRowSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const fetchSourceCollections = async (forceRefresh = false) => {
    setCollectionsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/import-manager/source-collections`, {
        params: {
          source_url: sourceUrl,
          force_refresh: forceRefresh,
        },
      });

      const collections = res.data.collections || [];
      setSourceCollections(collections);
      setFieldOptions(res.data.update_fields || []);
      setScrapeItemOptions(res.data.scrape_items || []);

      const defaults = (res.data.default_scrape_items || defaultScrapeItemKeys)
        .filter((key) => (res.data.scrape_items || []).some((item) => item.key === key));
      setSelectedScrapeItems(defaults.length > 0 ? defaults : (res.data.scrape_items || []).map((item) => item.key));

      if (collections.length > 0 && selectedCollectionUrls.length === 0) {
        setSelectedCollectionUrls([collections[0].url]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load source collections");
    }
    setCollectionsLoading(false);
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/import-manager/mapping-templates`);
      setTemplates(res.data.templates || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load mapping templates");
    }
  };

  useEffect(() => {
    fetchSourceCollections(false);
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!previewJob?.job_id) return;
    if (!["pending", "running"].includes(previewJob.status)) return;

    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/admin/import-manager/preview/${previewJob.job_id}`);
        const job = res.data;
        setPreviewJob(job);

        if (job.status === "completed") {
          const sourceFields = Object.keys(job.rows?.[0]?.scraped_values || {}).filter((key) => key !== "product_url" && key !== "gtm_raw");
          setMappings(buildSmartMapping(job.update_fields || fieldOptions, sourceFields));
          setSelectedRowIds((job.rows || []).map((row) => row.row_id));
          setPage(1);
          clearInterval(interval);
        }

        if (job.status === "failed") {
          toast.error(job.error || "Enhanced preview failed");
          clearInterval(interval);
        }
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to fetch preview status");
        clearInterval(interval);
      }
    }, 2400);

    return () => clearInterval(interval);
  }, [previewJob, fieldOptions]);

  const filteredCollections = useMemo(() => {
    const term = collectionSearch.trim().toLowerCase();
    if (!term) return sourceCollections;
    return sourceCollections.filter((collection) =>
      `${collection.name} ${collection.description || ""}`.toLowerCase().includes(term)
    );
  }, [sourceCollections, collectionSearch]);

  const sourceFieldOptions = useMemo(() => {
    if (!previewJob?.rows?.length) return [];
    return Object.keys(previewJob.rows[0]?.scraped_values || {}).filter((key) => key !== "product_url" && key !== "gtm_raw");
  }, [previewJob]);

  const filteredTargetFields = useMemo(() => {
    const term = targetFieldSearch.trim().toLowerCase();
    if (!term) return fieldOptions;
    return fieldOptions.filter((field) => `${field.key} ${field.label}`.toLowerCase().includes(term));
  }, [fieldOptions, targetFieldSearch]);

  const mappedRows = useMemo(() => {
    const rows = previewJob?.rows || [];
    return rows.map((row) => {
      const mappedValues = {};
      Object.entries(mappings || {}).forEach(([targetField, sources]) => {
        const resolved = resolveMappedValue(row.scraped_values || {}, sources);
        if (resolved !== null && resolved !== undefined) {
          mappedValues[targetField] = resolved;
        }
      });
      return {
        ...row,
        mapped_values: mappedValues,
      };
    });
  }, [previewJob, mappings]);

  const filteredMappedRows = useMemo(() => {
    const term = rowSearch.trim().toLowerCase();
    if (!term) return mappedRows;
    return mappedRows.filter((row) =>
      `${row.name || ""} ${row.collection_name || ""} ${row.product_url || ""}`.toLowerCase().includes(term)
    );
  }, [mappedRows, rowSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredMappedRows.length / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredMappedRows.slice(start, start + pageSize);
  }, [filteredMappedRows, page, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggleCollection = (url) => {
    setSelectedCollectionUrls((prev) => (prev.includes(url) ? prev.filter((item) => item !== url) : [...prev, url]));
  };

  const toggleScrapeItem = (key) => {
    setSelectedScrapeItems((prev) => (prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]));
  };

  const runEnhancedPreview = async () => {
    if (selectedCollectionUrls.length === 0) {
      toast.error("Please select at least one collection");
      return;
    }
    if (selectedScrapeItems.length === 0) {
      toast.error("Please select at least one scrape field");
      return;
    }

    setStartingPreview(true);
    setPublishResult(null);
    setConfirmPublish(false);
    try {
      const res = await axios.post(`${API}/admin/import-manager/preview`, {
        source_url: sourceUrl,
        collection_urls: selectedCollectionUrls,
        scrape_items: selectedScrapeItems,
      });
      setPreviewJob({
        job_id: res.data.job_id,
        status: res.data.status,
        total_collections: res.data.total_collections,
        selected_scrape_items: res.data.selected_scrape_items || selectedScrapeItems,
        scraped_collections: 0,
        total_products: 0,
        rows: [],
      });
      toast.success("Enhanced mapping preview started");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start enhanced preview");
    }
    setStartingPreview(false);
  };

  const updateMappingSlot = (targetField, slotIndex, sourceField) => {
    setMappings((prev) => {
      const current = prev[targetField] ? [...prev[targetField]] : ["", "", ""];
      current[slotIndex] = sourceField;
      return { ...prev, [targetField]: current };
    });
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRowIds((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  const toggleSelectVisibleRows = () => {
    const visibleIds = paginatedRows.map((row) => row.row_id);
    const allSelected = visibleIds.every((id) => selectedRowIds.includes(id));
    if (allSelected) {
      setSelectedRowIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedRowIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const saveTemplate = async () => {
    const cleaned = cleanMappings(mappings);
    if (Object.keys(cleaned).length === 0) {
      toast.error("Please configure at least one mapping before saving template");
      return;
    }
    if (!templateName.trim()) {
      toast.error("Please enter a template name");
      return;
    }

    setSavingTemplate(true);
    try {
      await axios.post(`${API}/admin/import-manager/mapping-templates`, {
        name: templateName,
        mappings: cleaned,
        is_default: templateIsDefault,
      });
      toast.success("Mapping template saved");
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save mapping template");
    }
    setSavingTemplate(false);
  };

  const applyTemplate = () => {
    const template = templates.find((item) => item.id === selectedTemplateId || item.name === selectedTemplateId);
    if (!template) return;

    const nextMappings = { ...mappings };
    Object.entries(template.mappings || {}).forEach(([target, sources]) => {
      nextMappings[target] = [sources?.[0] || "", sources?.[1] || "", sources?.[2] || ""];
    });
    setMappings(nextMappings);
    toast.success(`Template '${template.name}' applied`);
  };

  const publishWithMappings = async () => {
    if (!previewJob?.job_id || previewJob.status !== "completed") {
      toast.error("Preview must complete before publish");
      return;
    }
    if (selectedRowIds.length === 0) {
      toast.error("Please select at least one row");
      return;
    }
    if (!confirmPublish) {
      toast.error("Please confirm publish before updating products");
      return;
    }

    const cleanedMappings = cleanMappings(mappings);
    if (Object.keys(cleanedMappings).length === 0) {
      toast.error("Please configure at least one mapping");
      return;
    }

    setPublishing(true);
    try {
      const res = await axios.post(`${API}/admin/import-manager/enhanced-commit`, {
        job_id: previewJob.job_id,
        mappings: cleanedMappings,
        selected_row_ids: selectedRowIds,
        confirm_publish: true,
        replace_existing_images: replaceExistingImages,
      });
      setPublishResult(res.data);
      if (res.data.success) {
        toast.success("Enhanced mapping publish completed");
      } else {
        toast.error("Enhanced mapping publish completed with errors");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Enhanced publish failed");
    }
    setPublishing(false);
  };

  return (
    <div className="space-y-6" data-testid="import-manager-enhanced-mapping-tab-content">
      <div className="bg-white rounded-lg shadow-warm p-6" data-testid="enhanced-mapping-source-card">
        <div className="flex flex-col xl:flex-row gap-4 xl:items-end justify-between mb-4">
          <div className="flex-1">
            <label className="text-sm font-medium block mb-2">Source Collections URL</label>
            <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} data-testid="enhanced-mapping-source-url-input" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => fetchSourceCollections(true)} disabled={collectionsLoading} data-testid="enhanced-mapping-refresh-source-button">
              {collectionsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh
            </Button>
            <Button onClick={runEnhancedPreview} disabled={startingPreview || selectedCollectionUrls.length === 0} data-testid="enhanced-mapping-start-preview-button">
              {startingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Start Enhanced Preview
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input className="pl-9" placeholder="Search collections" value={collectionSearch} onChange={(e) => setCollectionSearch(e.target.value)} data-testid="enhanced-mapping-collection-search-input" />
            </div>
            <div className="border rounded-lg max-h-[300px] overflow-y-auto" data-testid="enhanced-mapping-collection-list">
              {filteredCollections.map((collection) => (
                <label key={collection.url} className="flex items-center gap-2 px-3 py-2 border-b text-sm">
                  <input type="checkbox" checked={selectedCollectionUrls.includes(collection.url)} onChange={() => toggleCollection(collection.url)} data-testid={`enhanced-mapping-collection-checkbox-${collection.name.replace(/\s+/g, "-").toLowerCase()}`} />
                  <span className="font-medium">{collection.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Scrape Fields</p>
            <div className="border rounded-lg max-h-[300px] overflow-y-auto p-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="enhanced-mapping-scrape-fields-list">
              {(scrapeItemOptions || []).map((item) => (
                <label key={item.key} className="flex items-center gap-2 px-2 py-1 text-sm border rounded">
                  <input type="checkbox" checked={selectedScrapeItems.includes(item.key)} onChange={() => toggleScrapeItem(item.key)} data-testid={`enhanced-mapping-scrape-item-checkbox-${item.key}`} />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {previewJob && (
        <div className="bg-white rounded-lg shadow-warm p-6" data-testid="enhanced-mapping-preview-status-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-600">Preview Job</p>
              <p className="font-mono text-xs text-gray-500" data-testid="enhanced-mapping-preview-job-id">{previewJob.job_id}</p>
            </div>
            <Badge data-testid="enhanced-mapping-preview-status-badge">{previewJob.status}</Badge>
          </div>
          <div className="w-full bg-gray-200 rounded h-2">
            <div className="bg-[#003366] h-2 rounded" style={{ width: `${previewJob.progress_percentage || 0}%` }} data-testid="enhanced-mapping-progress-bar" />
          </div>
        </div>
      )}

      {previewJob?.status === "completed" && (
        <>
          <div className="bg-white rounded-lg shadow-warm p-6" data-testid="enhanced-mapping-template-card">
            <h2 className="font-medium text-lg mb-3">Mapping Templates</h2>
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-3">
              <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Template name" data-testid="enhanced-mapping-template-name-input" />
              <label className="flex items-center gap-2 border rounded px-3 py-2 text-sm">
                <input type="checkbox" checked={templateIsDefault} onChange={(e) => setTemplateIsDefault(e.target.checked)} data-testid="enhanced-mapping-template-default-checkbox" />
                Set as default template
              </label>
              <Button onClick={saveTemplate} disabled={savingTemplate} data-testid="enhanced-mapping-save-template-button">
                {savingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Template
              </Button>
              <div className="flex gap-2">
                <select className="border rounded px-2 py-2 text-sm w-full" value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)} data-testid="enhanced-mapping-template-select">
                  <option value="">Select template</option>
                  {templates.map((template) => (
                    <option key={template.id || template.name} value={template.id || template.name}>{template.name}</option>
                  ))}
                </select>
                <Button variant="outline" onClick={applyTemplate} disabled={!selectedTemplateId} data-testid="enhanced-mapping-apply-template-button">Apply</Button>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-warm p-6" data-testid="enhanced-mapping-rules-card">
            <div className="flex flex-wrap gap-3 items-center justify-between mb-3">
              <h2 className="font-medium text-lg">Enhanced Mapping Rules (Priority Order)</h2>
              <Input value={targetFieldSearch} onChange={(e) => setTargetFieldSearch(e.target.value)} placeholder="Search DB target field" className="max-w-[300px]" data-testid="enhanced-mapping-target-field-search" />
            </div>

            <div className="overflow-auto max-h-[420px] border rounded" data-testid="enhanced-mapping-rules-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr>
                    <th className="text-left p-2">DB Target Field</th>
                    <th className="text-left p-2">Priority 1</th>
                    <th className="text-left p-2">Priority 2</th>
                    <th className="text-left p-2">Priority 3</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTargetFields.map((field) => {
                    const values = mappings[field.key] || ["", "", ""];
                    return (
                      <tr key={field.key} className="border-t" data-testid={`enhanced-mapping-target-row-${field.key}`}>
                        <td className="p-2 font-medium">{field.label}</td>
                        {prioritySlots.map((slotIndex) => (
                          <td key={`${field.key}-${slotIndex}`} className="p-2">
                            <select
                              className="border rounded px-2 py-1.5 text-sm w-full"
                              value={values[slotIndex] || ""}
                              onChange={(e) => updateMappingSlot(field.key, slotIndex, e.target.value)}
                              data-testid={`enhanced-mapping-select-${field.key}-priority-${slotIndex + 1}`}
                            >
                              <option value="">None</option>
                              {sourceFieldOptions.map((sourceField) => (
                                <option key={`${field.key}-${slotIndex}-${sourceField}`} value={sourceField}>{sourceField}</option>
                              ))}
                            </select>
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-warm p-6" data-testid="enhanced-mapping-preview-rows-card">
            <div className="flex flex-col xl:flex-row gap-3 justify-between mb-3">
              <div className="flex gap-2 items-center">
                <Input value={rowSearch} onChange={(e) => setRowSearch(e.target.value)} placeholder="Search rows" className="max-w-[280px]" data-testid="enhanced-mapping-row-search-input" />
                <Button variant="outline" onClick={toggleSelectVisibleRows} data-testid="enhanced-mapping-toggle-visible-rows-button">Toggle Visible</Button>
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-sm text-gray-600">Page Size</label>
                <select
                  className="border rounded px-2 py-2 text-sm"
                  value={pageSize}
                  onChange={(e) => {
                    const nextSize = Number(e.target.value);
                    setPageSize(nextSize);
                    setPage(1);
                  }}
                  data-testid="enhanced-mapping-page-size-select"
                >
                  {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
                <Badge variant="secondary" data-testid="enhanced-mapping-selected-rows-count">{selectedRowIds.length} selected</Badge>
              </div>
            </div>

            <div className="overflow-auto max-h-[520px] border rounded" data-testid="enhanced-mapping-preview-table">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr>
                    <th className="text-left p-2">Pick</th>
                    <th className="text-left p-2">Product</th>
                    <th className="text-left p-2">Collection</th>
                    <th className="text-left p-2">Source URL</th>
                    <th className="text-left p-2">Mapped Fields Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row) => (
                    <tr key={row.row_id} className="border-t align-top" data-testid={`enhanced-mapping-preview-row-${row.row_id}`}>
                      <td className="p-2">
                        <input type="checkbox" checked={selectedRowIds.includes(row.row_id)} onChange={() => toggleRowSelection(row.row_id)} data-testid={`enhanced-mapping-row-checkbox-${row.row_id}`} />
                      </td>
                      <td className="p-2 font-medium">{row.name}</td>
                      <td className="p-2">{row.collection_name}</td>
                      <td className="p-2 text-xs break-all text-blue-700">{row.product_url}</td>
                      <td className="p-2">
                        <pre className="text-xs whitespace-pre-wrap break-words bg-gray-50 p-2 rounded max-h-36 overflow-y-auto" data-testid={`enhanced-mapping-row-mapped-preview-${row.row_id}`}>
                          {JSON.stringify(row.mapped_values, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex items-center justify-between" data-testid="enhanced-mapping-pagination-controls">
              <p className="text-sm text-gray-600">Page {page} of {totalPages} • {filteredMappedRows.length} rows</p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))} data-testid="enhanced-mapping-pagination-prev-button">Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} data-testid="enhanced-mapping-pagination-next-button">Next</Button>
              </div>
            </div>

            <div className="mt-4 p-3 border rounded bg-gray-50" data-testid="enhanced-mapping-confirm-publish-block">
              <div className="flex items-start gap-2 text-sm mb-3">
                <input type="checkbox" checked={replaceExistingImages} onChange={(e) => setReplaceExistingImages(e.target.checked)} data-testid="enhanced-mapping-replace-images-checkbox" />
                <span>Replace existing DB images when mapped value updates <strong>images</strong> field.</span>
              </div>
              <div className="flex items-start gap-2 text-sm mb-3">
                <input type="checkbox" checked={confirmPublish} onChange={(e) => setConfirmPublish(e.target.checked)} data-testid="enhanced-mapping-confirm-publish-checkbox" />
                <span>I confirm mapping rules and selected rows are reviewed and ready to publish.</span>
              </div>

              <Button
                onClick={publishWithMappings}
                disabled={publishing || selectedRowIds.length === 0 || !confirmPublish}
                data-testid="enhanced-mapping-publish-button"
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Publish with Enhanced Mapping
              </Button>
            </div>

            {publishResult && (
              <div className={`mt-4 p-4 rounded border ${publishResult.success ? "bg-green-50 border-green-200" : "bg-yellow-50 border-yellow-200"}`} data-testid="enhanced-mapping-publish-result-card">
                <div className="flex items-center gap-2 mb-2">
                  {publishResult.success ? <CheckCircle className="w-5 h-5 text-green-700" /> : <RefreshCw className="w-5 h-5 text-yellow-700" />}
                  <p className="font-medium">Enhanced Publish Result</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div data-testid="enhanced-mapping-publish-inserted-count">Inserted: <strong>{publishResult.inserted}</strong></div>
                  <div data-testid="enhanced-mapping-publish-updated-count">Updated: <strong>{publishResult.updated}</strong></div>
                  <div data-testid="enhanced-mapping-publish-skipped-count">Skipped: <strong>{publishResult.skipped}</strong></div>
                  <div data-testid="enhanced-mapping-publish-errors-count">Errors: <strong>{publishResult.errors?.length || 0}</strong></div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
